import { useCallback, useEffect, useMemo, useState } from "react";

import { useWhatsappConfiguration } from "V2.0/HomePages/Integrations/Whatsapp/models/useWhatsappConfiguration";

import WhatsappAssistantVoiceMode from "./WhatsappAssistantVoiceMode";
import WhatsappAssistantDefault from "../WhatsappAssistantChat";

import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useAssistants } from "../../Assistants";

function useWhatsappAssistantMode(
  communicationBinding,
  metaWhatsappConfiguration,
) {
  const [isVoiceModeAvailable, setIsVoiceModeAvailable] = useState({
    isLoading: true,
    isAvailable: false,
  });

  const phoneNumbers = useMemo(
    function () {
      return communicationBinding.map(function (binding) {
        return binding.identifier;
      });
    },
    [communicationBinding],
  );

  const checkVoiceModeAvailability = useCallback(function (
    phoneNumbers,
    metaWhatsappConfiguration,
  ) {
    if (
      phoneNumbers.length === 0 ||
      (!metaWhatsappConfiguration?.loading &&
        !metaWhatsappConfiguration?.isConfigured)
    ) {
      setIsVoiceModeAvailable({ isAvailable: false, isLoading: false });
      return;
    }

    if (metaWhatsappConfiguration.loading) {
      setIsVoiceModeAvailable({ isAvailable: false, isLoading: true });
      return;
    }

    const groupAvailableNumbersByVoiceCallEnabledStatus =
      metaWhatsappConfiguration.data?.phoneNumbersVoiceEnabled
        ? metaWhatsappConfiguration.data?.phoneNumbersVoiceEnabled?.reduce(
            function (acc, { id, voiceCallingEnabled }) {
              voiceCallingEnabled
                ? acc.enabled.push(id)
                : acc.disabled.push(id);

              return acc;
            },
            { enabled: [], disabled: [] },
          )
        : { enabled: [], disabled: [] };

    const isAvailable = phoneNumbers.some(function (_phoneNumber) {
      return groupAvailableNumbersByVoiceCallEnabledStatus.enabled.includes(
        _phoneNumber,
      );
    });

    setIsVoiceModeAvailable({ isAvailable, isLoading: false });
    return;
  }, []);

  useEffect(
    function () {
      checkVoiceModeAvailability(phoneNumbers, metaWhatsappConfiguration);
    },
    [phoneNumbers, metaWhatsappConfiguration],
  );

  const value = useMemo(
    function () {
      return {
        isVoiceModeAvailable,
        checkVoiceModeAvailability,
      };
    },
    [isVoiceModeAvailable, checkVoiceModeAvailability],
  );

  return value;
}

export default function WhatsappAssistant(props) {
  const { communicationBinding, workspaceId } = props;

  const { whatsappConfiguration } = useWhatsappConfiguration(workspaceId);

  const { isVoiceModeAvailable } = useWhatsappAssistantMode(
    communicationBinding,
    whatsappConfiguration,
  );

  const { assistants, fetchAssistants } = useAssistants(workspaceId);

  useEffect(() => {
    if (workspaceId) {
      fetchAssistants();
    }
  }, [workspaceId]);

  const AssistantComponent = useMemo(
    function () {
      if (isVoiceModeAvailable.isLoading) {
        return () => (
          <div className="flex h-full w-full items-center justify-center">
            <AiOutlineLoading3Quarters className="size-8 animate-spin" />
          </div>
        );
      }

      return isVoiceModeAvailable.isAvailable
        ? WhatsappAssistantVoiceMode
        : WhatsappAssistantDefault;
    },
    [isVoiceModeAvailable],
  );

  return (
    <AssistantComponent
      {...props}
      assistants={assistants}
    />
  );
}
